import React, { useState, useEffect } from 'react';
import './CardEditor.css';
import { useAuth } from '@/hooks/useAuth';
import { BusinessCardData, PhoneNumber, Address, SocialProfile } from '@/types';
import { 
  Loading, 
  QRModal, 
  FormSectionHeader, 
  ValidatedInput, 
  AddressForm, 
  DynamicFieldList 
} from '@/components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCardsStore, useProfileStore, useQRStore } from '@/stores';
import { 
  Plus, X, Mail, MapPin, Globe, 
  User, AlertCircle, CheckCircle2,
  Image, FileText, Share2, Copy,
  Save, ArrowLeft, QrCode, Edit, Trash2
} from 'lucide-react';
import jsPDF from 'jspdf';
import { downloadVCard } from '@/utils/vcard.utils';
import { createBrandedCardImage, generateCardFileName } from '@/utils/cardImage.utils';
import { Country, State } from 'country-state-city';

const CardEditor: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const location = useLocation();
  const { cardId } = useParams<{ cardId: string }>();
  
  // Zustand stores
  const getCardById = useCardsStore(state => state.getCardById);
  const createCard = useCardsStore(state => state.createCard);
  const updateCard = useCardsStore(state => state.updateCard);
  const canCreateCard = useCardsStore(state => state.canCreateCard);
  const maxCardsLimit = useCardsStore(state => state.maxCardsLimit);
  const azureProfile = useProfileStore(state => state.azureProfile);
  const companyInfo = useProfileStore(state => state.companyInfo);
  const fetchProfile = useProfileStore(state => state.fetchProfile);
  const generateQR = useQRStore(state => state.generateQR);
  const getCachedOrGenerate = useQRStore(state => state.getCachedOrGenerate);
  
  // Navigation state
  const isNewCard: boolean = location.state?.isNewCard || false;
  const isEdit: boolean = location.state?.isEdit || !!cardId;
  const isViewMode: boolean = location.state?.isViewMode || false; // New: view-only mode
  const fromRoute: string = location.state?.fromRoute || '/';
  const justCreated: boolean = location.state?.justCreated || false;
  const scannedCardData: BusinessCardData | undefined = location.state?.scannedCardData;
  const scannedCardName: string | undefined = location.state?.scannedCardName;
  const isFromScan = !!scannedCardData;
  
  // Get existing card from store if editing
  const existingCard = cardId ? getCardById(parseInt(cardId)) : undefined;

  // State to track if currently in edit or view mode
  // Start in view mode if isViewMode is true, otherwise in edit mode
  const [isEditMode, setIsEditMode] = useState<boolean>(!isViewMode || isNewCard);

  // Clean up empty fields from form data
  const cleanupFormData = () => {
    setFormData(prev => ({
      ...prev,
      additionalEmails: filterEmptyEmails(prev.additionalEmails || []),
      additionalPhones: filterEmptyPhones(prev.additionalPhones || []),
      address: prev.address && (
        prev.address.street?.trim() || 
        prev.address.city?.trim() || 
        prev.address.state?.trim() || 
        prev.address.postalCode?.trim() || 
        prev.address.country?.trim()
      ) ? prev.address : undefined,
      additionalAddresses: filterEmptyAddresses(prev.additionalAddresses || []),
      socialProfiles: filterEmptySocialProfiles(prev.socialProfiles || []),
      website: prev.website?.trim() || '',
      notes: prev.notes?.trim() || '',
    }));
  };

  // Toggle between edit and view modes
  const handleToggleEditMode = () => {
    if (isEditMode) {
      // Switching from edit to view — revert to original saved data
      setFormData(initializeFormData());
      setCardName(existingCard?.cardName || '');
      setIsDirty(false);
    }
    setIsEditMode(prev => !prev);
    // Clear errors and validation when toggling mode
    setError(null);
    if (!isEditMode) {
      setValidationErrors({});
    }
  };

  // Initialize form data from existing card, scanned data, or Azure profile
  const initializeFormData = (): BusinessCardData => {
    // Try to get card from store first
    const card = existingCard;
    
    if (card) {
      const primaryEmail = card.emails.find(e => e.is_primary)?.email || card.emails[0]?.email;
      const primaryPhone = card.phones.find(p => p.is_primary)?.number || card.phones[0]?.number;
      
      // Fall back to Azure AD address if card has none stored
      let cardAddress = card.addresses[0];
      if (!cardAddress && azureProfile && (azureProfile.streetAddress || azureProfile.city || azureProfile.country)) {
        cardAddress = {
          type: 'WORK',
          street: azureProfile.streetAddress,
          city: azureProfile.city,
          state: azureProfile.state,
          postalCode: azureProfile.postalCode,
          country: azureProfile.country,
        };
      }
      
      return {
        displayName: card.displayName || '',
        givenName: card.givenName || '',
        surname: card.surname || '',
        jobTitle: card.jobTitle || '',
        companyName: card.companyName || '',
        department: card.department || '',
        mail: primaryEmail || '',
        mobilePhone: primaryPhone || '',
        businessPhones: [],
        additionalEmails: card.emails.filter(e => !e.is_primary).map(e => e.email),
        additionalPhones: card.phones.filter(p => !p.is_primary).map(p => ({ type: p.type, number: p.number })),
        address: cardAddress,
        additionalAddresses: card.addresses.slice(1),
        website: card.website || '',
        socialProfiles: card.socialProfiles || [],
        notes: card.notes || '',
      };
    } else if (scannedCardData) {
      return scannedCardData;
    } else if (azureProfile) {
      // Build address from Azure profile for new cards
      const profileAddress = (azureProfile.streetAddress || azureProfile.city || azureProfile.country)
        ? { type: 'WORK' as const, street: azureProfile.streetAddress, city: azureProfile.city, state: azureProfile.state, postalCode: azureProfile.postalCode, country: azureProfile.country }
        : undefined;

      return {
        displayName: azureProfile.displayName || '',
        givenName: azureProfile.givenName || '',
        surname: azureProfile.surname || '',
        jobTitle: azureProfile.jobTitle || '',
        companyName: azureProfile.companyName || '',
        department: azureProfile.department || '',
        mail: azureProfile.mail || '',
        mobilePhone: azureProfile.mobilePhone || '',
        businessPhones: azureProfile.businessPhones || [],
        additionalEmails: [],
        additionalPhones: [],
        address: profileAddress,
        additionalAddresses: [],
        website: '',
        socialProfiles: [],
        notes: '',
      };
    }
    return {
      displayName: '',
      additionalEmails: [],
      additionalPhones: [],
      additionalAddresses: [],
      socialProfiles: [],
    };
  };

  const [cardName, setCardName] = useState(existingCard?.cardName || scannedCardName || '');
  const [formData, setFormData] = useState<BusinessCardData>(initializeFormData());
  const [currentCardId, setCurrentCardId] = useState<number | undefined>(
    existingCard?.cardId || (cardId ? parseInt(cardId) : undefined)
  );

  const [qrCode, setQrCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);

  const [primaryColor] = useState('#1E3A8A');
  const [secondaryColor] = useState('#FFFFFF');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showEmptyFieldsConfirm, setShowEmptyFieldsConfirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Update edit mode when navigation state changes
  useEffect(() => {
    setIsEditMode(!isViewMode || isNewCard);
  }, [isViewMode, isNewCard]);

  // Re-fill address from Azure profile once it loads (if card has no address)
  useEffect(() => {
    if (azureProfile && existingCard && existingCard.addresses.length === 0) {
      if (azureProfile.streetAddress || azureProfile.city || azureProfile.country) {
        setFormData(prev => {
          if (!prev.address) {
            return {
              ...prev,
              address: {
                type: 'WORK',
                street: azureProfile.streetAddress,
                city: azureProfile.city,
                state: azureProfile.state,
                postalCode: azureProfile.postalCode,
                country: azureProfile.country,
              },
            };
          }
          return prev;
        });
      }
    }
  }, [azureProfile, existingCard]);

  // #38: Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty && isEditMode) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isEditMode]);

  // #42: Auto-clear errors after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Validation helper functions
  const validateEmail = (email: string): boolean => {
    if (!email.trim()) return true; // Empty is valid (optional field)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone.trim()) return true; // Empty is valid (optional field)
    // Remove spaces and check if it contains only valid characters
    const cleanPhone = phone.replace(/\s/g, '');
    // Must contain only numbers, +, (), -, and spaces, and have at least 7 digits
    const validCharsRegex = /^[+\d\s\-\(\)\.]+$/;
    const digitsOnly = cleanPhone.replace(/[^0-9]/g, '');
    
    if (!validCharsRegex.test(phone)) return false;
    if (digitsOnly.length < 7 || digitsOnly.length > 15) return false;
    
    return true;
  };

  const validateURL = (url: string): boolean => {
    if (!url.trim()) return true; // Empty is valid (optional field)
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateCountry = (country: string): boolean => {
    if (!country.trim()) return true; // Empty is valid (optional field)
    const countries = Country.getAllCountries();
    const countryNames = countries.map(c => c.name.toLowerCase());
    const countryCodes = countries.map(c => c.isoCode.toLowerCase());
    const searchTerm = country.toLowerCase().trim();
    return countryNames.includes(searchTerm) || countryCodes.includes(searchTerm);
  };

  const validateState = (state: string, country: string): boolean => {
    if (!state.trim()) return true; // Empty is valid (optional field)
    if (!country.trim()) return true; // Can't validate state without country
    
    // Find the country
    const countries = Country.getAllCountries();
    const foundCountry = countries.find(c => 
      c.name.toLowerCase() === country.toLowerCase().trim() || 
      c.isoCode.toLowerCase() === country.toLowerCase().trim()
    );
    
    if (!foundCountry) return true; // If country is invalid, don't validate state yet
    
    // Get states for this country
    const states = State.getStatesOfCountry(foundCountry.isoCode);
    if (states.length === 0) return true; // Country has no states/provinces
    
    const stateNames = states.map(s => s.name.toLowerCase());
    const stateCodes = states.map(s => s.isoCode.toLowerCase());
    const searchTerm = state.toLowerCase().trim();
    
    return stateNames.includes(searchTerm) || stateCodes.includes(searchTerm);
  };

  const validatePostalCode = (postalCode: string, country: string): boolean => {
    if (!postalCode.trim()) return true; // Empty is valid (optional field)
    if (!country.trim()) return true; // Can't validate without country
    
    const code = postalCode.trim();
    const countries = Country.getAllCountries();
    const foundCountry = countries.find(c => 
      c.name.toLowerCase() === country.toLowerCase().trim() || 
      c.isoCode.toLowerCase() === country.toLowerCase().trim()
    );
    
    if (!foundCountry) return true; // If country is invalid, don't validate postal code yet
    
    // Common postal code patterns by country ISO code
    const postalPatterns: Record<string, RegExp> = {
      'US': /^\d{5}(-\d{4})?$/, // 12345 or 12345-6789
      'CA': /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, // A1A 1A1
      'GB': /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i, // SW1A 1AA
      'IN': /^\d{6}$/, // 110001
      'AU': /^\d{4}$/, // 2000
      'DE': /^\d{5}$/, // 12345
      'FR': /^\d{5}$/, // 75001
      'IT': /^\d{5}$/, // 00100
      'ES': /^\d{5}$/, // 28001
      'NL': /^\d{4}\s?[A-Z]{2}$/i, // 1234 AB
      'BR': /^\d{5}-?\d{3}$/, // 12345-678
      'MX': /^\d{5}$/, // 12345
      'JP': /^\d{3}-?\d{4}$/, // 123-4567
      'CN': /^\d{6}$/, // 100000
      'ZA': /^\d{4}$/, // 0001
      'NZ': /^\d{4}$/, // 1010
      'SE': /^\d{3}\s?\d{2}$/, // 123 45
      'CH': /^\d{4}$/, // 1234
      'AT': /^\d{4}$/, // 1010
      'BE': /^\d{4}$/, // 1000
      'DK': /^\d{4}$/, // 1000
      'NO': /^\d{4}$/, // 0001
      'FI': /^\d{5}$/, // 00100
      'PL': /^\d{2}-?\d{3}$/, // 00-950
      'PT': /^\d{4}-?\d{3}$/, // 1000-001
      'IE': /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/i, // D02 AF30
      'SG': /^\d{6}$/, // 018956
      'MY': /^\d{5}$/, // 50000
      'PH': /^\d{4}$/, // 1000
      'TH': /^\d{5}$/, // 10100
      'KR': /^\d{5}$/, // 03000
      'TR': /^\d{5}$/, // 34000
      'RU': /^\d{6}$/, // 101000
      'IL': /^\d{7}$/, // 1234567
      'AE': /^\d{5}$/, // 12345
      'SA': /^\d{5}$/, // 11564
    };
    
    const pattern = postalPatterns[foundCountry.isoCode];
    if (pattern) {
      return pattern.test(code);
    }
    
    // Generic validation for countries without specific patterns
    // Most postal codes are 3-10 characters with letters, numbers, spaces, and hyphens
    return /^[A-Za-z0-9\s-]{3,10}$/.test(code);
  };

  const validateRequired = (value: string | undefined): boolean => {
    return !!value && value.trim().length > 0;
  };

  // Filter empty values from arrays
  const filterEmptyEmails = (emails: string[]): string[] => {
    return emails.filter(email => email && email.trim().length > 0);
  };

  const filterEmptyPhones = (phones: PhoneNumber[]): PhoneNumber[] => {
    return phones.filter(phone => phone.number && phone.number.trim().length > 0);
  };

  const filterEmptyAddresses = (addresses: Address[]): Address[] => {
    // Only include addresses where ALL required fields are filled
    return addresses.filter(addr => 
      addr.street?.trim() && 
      addr.city?.trim() && 
      addr.state?.trim() && 
      addr.postalCode?.trim() && 
      addr.country?.trim()
    );
  };

  const filterEmptySocialProfiles = (profiles: SocialProfile[]): SocialProfile[] => {
    return profiles.filter(profile => profile.url && profile.url.trim().length > 0);
  };

  // Validate all form data
  const validateFormData = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!validateRequired(cardName)) {
      errors.cardName = 'Card name is required';
    } else if (cardName.trim().length > 50) {
      errors.cardName = 'Card name must be 50 characters or less';
    }
    if (!validateRequired(formData.displayName)) {
      errors.displayName = 'Display name is required';
    }

    // Phone is mandatory
    if (!formData.mobilePhone || !formData.mobilePhone.trim()) {
      errors.mobilePhone = 'Phone number is required';
    }

    // Email validation
    if (formData.mail && !validateEmail(formData.mail)) {
      errors.mail = 'Invalid email format';
    }

    // Additional emails
    formData.additionalEmails?.forEach((email, index) => {
      if (email.trim() && !validateEmail(email)) {
        errors[`additionalEmail_${index}`] = 'Invalid email format';
      }
    });

    // Phone validation
    if (formData.mobilePhone && !validatePhone(formData.mobilePhone)) {
      const digitsOnly = formData.mobilePhone.replace(/[^0-9]/g, '');
      if (digitsOnly.length < 7) {
        errors.mobilePhone = 'Phone number too short (min 7 digits)';
      } else if (digitsOnly.length > 15) {
        errors.mobilePhone = 'Phone number too long (max 15 digits)';
      } else {
        errors.mobilePhone = 'Invalid phone format (only numbers, +, -, (), . allowed)';
      }
    }

    // Additional phones
    formData.additionalPhones?.forEach((phone, index) => {
      if (phone.number.trim() && !validatePhone(phone.number)) {
        const digitsOnly = phone.number.replace(/[^0-9]/g, '');
        if (digitsOnly.length < 7) {
          errors[`additionalPhone_${index}`] = 'Phone number too short (min 7 digits)';
        } else if (digitsOnly.length > 15) {
          errors[`additionalPhone_${index}`] = 'Phone number too long (max 15 digits)';
        } else {
          errors[`additionalPhone_${index}`] = 'Invalid phone format (only numbers, +, -, (), . allowed)';
        }
      }
    });

    // Website validation
    if (formData.website && !validateURL(formData.website)) {
      errors.website = 'Invalid URL format (must include http:// or https://)';
    }

    // Social profiles validation
    formData.socialProfiles?.forEach((profile, index) => {
      if (profile.url.trim() && !validateURL(profile.url)) {
        errors[`socialProfile_${index}`] = 'Invalid URL format (must include http:// or https://)';
      }
    });

    // Primary address validation
    if (formData.address) {
      const hasAnyAddressField = 
        formData.address.street?.trim() || 
        formData.address.city?.trim() || 
        formData.address.state?.trim() || 
        formData.address.postalCode?.trim() || 
        formData.address.country?.trim();
      
      // If any field is filled, all fields must be filled
      if (hasAnyAddressField) {
        if (!formData.address.street?.trim()) {
          errors['address_primary_street'] = 'Street address is required when adding an address.';
        }
        if (!formData.address.city?.trim()) {
          errors['address_primary_city'] = 'City is required when adding an address.';
        }
        if (!formData.address.state?.trim()) {
          errors['address_primary_state'] = 'State/Province is required when adding an address.';
        }
        if (!formData.address.country?.trim()) {
          errors['address_primary_country'] = 'Country is required when adding an address.';
        }
        
        // Validate format of filled fields
        if (formData.address.country && formData.address.country.trim() && !validateCountry(formData.address.country)) {
          errors['address_primary_country'] = 'Invalid country name. Please select a valid country.';
        }
        if (formData.address.state && formData.address.state.trim() && formData.address.country) {
          if (!validateState(formData.address.state, formData.address.country)) {
            errors['address_primary_state'] = 'Invalid state/province for the selected country.';
          }
        }
        if (formData.address.postalCode && formData.address.postalCode.trim() && formData.address.country) {
          if (!validatePostalCode(formData.address.postalCode, formData.address.country)) {
            errors['address_primary_postalCode'] = 'Invalid postal code format for the selected country.';
          }
        }
      }
    }

    // Additional addresses validation
    formData.additionalAddresses?.forEach((address, index) => {
      const hasAnyAddressField = 
        address.street?.trim() || 
        address.city?.trim() || 
        address.state?.trim() || 
        address.postalCode?.trim() || 
        address.country?.trim();
      
      // If any field is filled, all fields must be filled
      if (hasAnyAddressField) {
        if (!address.street?.trim()) {
          errors[`address_${index}_street`] = 'Street address is required when adding an address.';
        }
        if (!address.city?.trim()) {
          errors[`address_${index}_city`] = 'City is required when adding an address.';
        }
        if (!address.state?.trim()) {
          errors[`address_${index}_state`] = 'State/Province is required when adding an address.';
        }
        if (!address.country?.trim()) {
          errors[`address_${index}_country`] = 'Country is required when adding an address.';
        }
        
        // Validate format of filled fields
        if (address.country && address.country.trim() && !validateCountry(address.country)) {
          errors[`address_${index}_country`] = 'Invalid country name. Please select a valid country.';
        }
        if (address.state && address.state.trim() && address.country) {
          if (!validateState(address.state, address.country)) {
            errors[`address_${index}_state`] = 'Invalid state/province for the selected country.';
          }
        }
        if (address.postalCode && address.postalCode.trim() && address.country) {
          if (!validatePostalCode(address.postalCode, address.country)) {
            errors[`address_${index}_postalCode`] = 'Invalid postal code format for the selected country.';
          }
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Load card data if editing via URL parameter
  useEffect(() => {
    const loadData = async () => {
      // If we have a cardId but no card in store yet, the store needs to fetch cards
      if (cardId && !existingCard) {
        try {
          setLoading(true);
          const token = await getAccessToken(true);
          // This will populate the cards store
          const cardsStore = useCardsStore.getState();
          await cardsStore.fetchCards(token);
          
          // Now get the card from store
          const card = cardsStore.getCardById(parseInt(cardId));
          if (card) {
            setCardName(card.cardName);
            setCurrentCardId(card.cardId);
            
            // Update form with card data
            const primaryEmail = card.emails.find(e => e.is_primary)?.email || card.emails[0]?.email;
            const primaryPhone = card.phones.find(p => p.is_primary)?.number || card.phones[0]?.number;
            
            setFormData({
              displayName: card.displayName || '',
              givenName: card.givenName || '',
              surname: card.surname || '',
              jobTitle: card.jobTitle || '',
              companyName: card.companyName || '',
              department: card.department || '',
              mail: primaryEmail || '',
              mobilePhone: primaryPhone || '',
              businessPhones: [],
              additionalEmails: card.emails.filter(e => !e.is_primary).map(e => e.email),
              additionalPhones: card.phones.filter(p => !p.is_primary).map(p => ({ type: p.type, number: p.number })),
              address: card.addresses[0],
              additionalAddresses: card.addresses.slice(1),
              website: card.website || '',
              socialProfiles: card.socialProfiles || [],
              notes: card.notes || '',
            });
          } else {
            setError('Card not found');
            setTimeout(() => navigate('/cards'), 2000);
          }
        } catch (err) {
          console.error('Failed to load card:', err);
          setError('Failed to load card details');
          setTimeout(() => navigate('/cards'), 2000);
        } finally {
          setLoading(false);
        }
      }
    };

    loadData();
  }, [cardId]);

  // Load Azure profile only for new card creation (not for editing)
  useEffect(() => {
    const loadAzureProfile = async () => {
      // Only load Azure profile if creating a new card and profile not already in store
      if (!azureProfile && isNewCard && !existingCard) {
        try {
          setLoading(true);
          const token = await getAccessToken(true);
          await fetchProfile(token);
          
          // Profile is now in store, update form data
          const profile = useProfileStore.getState().azureProfile;
          if (profile) {
            setFormData({
              displayName: profile.displayName || '',
              givenName: profile.givenName || '',
              surname: profile.surname || '',
              jobTitle: profile.jobTitle || '',
              companyName: profile.companyName || '',
              department: profile.department || '',
              mail: profile.mail || '',
              mobilePhone: profile.mobilePhone || '',
              businessPhones: profile.businessPhones || [],
              additionalEmails: [],
              additionalPhones: [],
              address: undefined,
              additionalAddresses: [],
              website: '',
              socialProfiles: [],
              notes: '',
            });
          }
        } catch (err) {
          console.error('Failed to load Azure profile:', err);
          setError('Failed to load profile. Please try refreshing the page.');
        } finally {
          setLoading(false);
        }
      }
    };

    loadAzureProfile();
  }, []);

  // Generate QR code on mount if editing existing card or if just created
  useEffect(() => {
    const generateInitialQR = async () => {
      if (existingCard && existingCard.cardId) {
        try {
          setLoading(true);
          const token = await getAccessToken(true);
          
          // Use store method which handles caching and existing QR URLs
          const qrData = await getCachedOrGenerate(
            existingCard.cardId,
            token,
            existingCard.qrCodeUrl
          );
          
          // Set QR code from store data
          setQrCode(qrData.vCardQR);
          
          // Show success message if just created
          if (justCreated) {
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
          }
        } catch (err) {
          console.error('Failed to generate initial QR:', err);
          setError('Failed to generate QR code. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    };

    if (existingCard) {
      generateInitialQR();
    }
  }, [existingCard?.cardId]);

  const handleInputChange = (field: keyof BusinessCardData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  // Helper function to clear a specific validation error
  const clearValidationError = (errorKey: string) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });
  };

  // Additional Emails
  const addEmail = () => {
    if ((formData.additionalEmails?.length ?? 0) >= 5) return;
    setFormData(prev => ({
      ...prev,
      additionalEmails: [...(prev.additionalEmails || []), '']
    }));
  };

  const updateEmail = (index: number, value: string) => {
    const emails = [...(formData.additionalEmails || [])];
    emails[index] = value;
    setFormData(prev => ({ ...prev, additionalEmails: emails }));
  };

  const removeEmail = (index: number) => {
    const emails = [...(formData.additionalEmails || [])];
    emails.splice(index, 1);
    setFormData(prev => ({ ...prev, additionalEmails: emails }));
    
    // Clear validation errors for this email and adjust indices for subsequent emails
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      // Remove error for the deleted email
      delete newErrors[`additionalEmail_${index}`];
      
      // Shift error keys for subsequent emails
      for (let i = index + 1; i < formData.additionalEmails!.length; i++) {
        if (newErrors[`additionalEmail_${i}`]) {
          newErrors[`additionalEmail_${i - 1}`] = newErrors[`additionalEmail_${i}`];
          delete newErrors[`additionalEmail_${i}`];
        }
      }
      return newErrors;
    });
  };

  // Additional Phones
  const addPhone = () => {
    if ((formData.additionalPhones?.length ?? 0) >= 5) return;
    setFormData(prev => ({
      ...prev,
      additionalPhones: [...(prev.additionalPhones || []), { type: 'HOME', number: '' }]
    }));
  };

  const updatePhone = (index: number, field: keyof PhoneNumber, value: string) => {
    const phones = [...(formData.additionalPhones || [])];
    phones[index] = { ...phones[index], [field]: value };
    setFormData(prev => ({ ...prev, additionalPhones: phones }));
  };

  const removePhone = (index: number) => {
    const phones = [...(formData.additionalPhones || [])];
    phones.splice(index, 1);
    setFormData(prev => ({ ...prev, additionalPhones: phones }));
    
    // Clear validation errors for this phone and adjust indices for subsequent phones
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      // Remove error for the deleted phone
      delete newErrors[`additionalPhone_${index}`];
      
      // Shift error keys for subsequent phones
      for (let i = index + 1; i < formData.additionalPhones!.length; i++) {
        if (newErrors[`additionalPhone_${i}`]) {
          newErrors[`additionalPhone_${i - 1}`] = newErrors[`additionalPhone_${i}`];
          delete newErrors[`additionalPhone_${i}`];
        }
      }
      return newErrors;
    });
  };

  // Addresses
  const addAddress = (isPrimary: boolean = false) => {
    const newAddress: Address = {
      type: 'WORK',
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    };

    if (isPrimary) {
      setFormData(prev => ({ ...prev, address: newAddress }));
    } else {
      if ((formData.additionalAddresses?.length ?? 0) >= 4) return;
      setFormData(prev => ({
        ...prev,
        additionalAddresses: [...(prev.additionalAddresses || []), newAddress]
      }));
    }
  };

  const updateAddress = (index: number | 'primary', field: keyof Address, value: string) => {
    if (index === 'primary') {
      setFormData(prev => ({
        ...prev,
        address: { ...(prev.address || {} as Address), [field]: value }
      }));
    } else {
      const addresses = [...(formData.additionalAddresses || [])];
      addresses[index as number] = { ...addresses[index as number], [field]: value };
      setFormData(prev => ({ ...prev, additionalAddresses: addresses }));
    }
  };

  const removeAddress = (index: number | 'primary') => {
    if (!window.confirm('Are you sure you want to remove this address?')) return;
    if (index === 'primary') {
      setFormData(prev => ({ ...prev, address: undefined }));
    } else {
      const addresses = [...(formData.additionalAddresses || [])];
      addresses.splice(index as number, 1);
      setFormData(prev => ({ ...prev, additionalAddresses: addresses }));
    }
  };

  // Social Profiles
  const addSocialProfile = () => {
    if ((formData.socialProfiles?.length ?? 0) >= 4) return;
    setFormData(prev => ({
      ...prev,
      socialProfiles: [...(prev.socialProfiles || []), { platform: 'LinkedIn', url: '' }]
    }));
  };

  const updateSocialProfile = (index: number, field: keyof SocialProfile, value: string) => {
    const profiles = [...(formData.socialProfiles || [])];
    profiles[index] = { ...profiles[index], [field]: value };
    setFormData(prev => ({ ...prev, socialProfiles: profiles }));
  };

  const removeSocialProfile = (index: number) => {
    const profiles = [...(formData.socialProfiles || [])];
    profiles.splice(index, 1);
    setFormData(prev => ({ ...prev, socialProfiles: profiles }));
    
    // Clear validation errors for this profile and adjust indices for subsequent profiles
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      // Remove error for the deleted profile
      delete newErrors[`socialProfile_${index}`];
      
      // Shift error keys for subsequent profiles
      for (let i = index + 1; i < formData.socialProfiles!.length; i++) {
        if (newErrors[`socialProfile_${i}`]) {
          newErrors[`socialProfile_${i - 1}`] = newErrors[`socialProfile_${i}`];
          delete newErrors[`socialProfile_${i}`];
        }
      }
      return newErrors;
    });
  };

  const handleGenerateQR = async () => {
    // Validate all inputs
    if (!validateFormData()) {
      setError('Please fix the validation errors before saving');
      return;
    }

    // Check for empty added rows — warn user before discarding
    const emptyEmails = (formData.additionalEmails || []).filter(e => !e || !e.trim()).length;
    const emptyPhones = (formData.additionalPhones || []).filter(p => !p.number || !p.number.trim()).length;
    const emptySocials = (formData.socialProfiles || []).filter(p => !p.url || !p.url.trim()).length;
    const hasEmptyRows = emptyEmails + emptyPhones + emptySocials > 0;

    if (hasEmptyRows && !showEmptyFieldsConfirm) {
      setShowEmptyFieldsConfirm(true);
      return;
    }
    setShowEmptyFieldsConfirm(false);

    // Check card limit for new cards
    if ((isNewCard || !currentCardId) && !canCreateCard()) {
      setError(`You have reached the maximum limit of ${maxCardsLimit} cards. Please delete an existing card to create a new one.`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const token = await getAccessToken(true);
      let cardIdToUse = currentCardId;

      // Filter out empty values from arrays
      const cleanedFormData: BusinessCardData = {
        ...formData,
        additionalEmails: filterEmptyEmails(formData.additionalEmails || []),
        additionalPhones: filterEmptyPhones(formData.additionalPhones || []),
        address: formData.address && (
          formData.address.street?.trim() || 
          formData.address.city?.trim() || 
          formData.address.state?.trim() || 
          formData.address.postalCode?.trim() || 
          formData.address.country?.trim()
        ) ? formData.address : undefined,
        additionalAddresses: filterEmptyAddresses(formData.additionalAddresses || []),
        socialProfiles: filterEmptySocialProfiles(formData.socialProfiles || []),
      };

      // Step 1: Create or Update the card using store
      if (isNewCard || !currentCardId) {
        // Create new card
        cardIdToUse = await createCard({
          cardName: cardName.trim(),
          cardData: cleanedFormData,
          isDefault: false,
        }, token);

        setCurrentCardId(cardIdToUse);
      } else {
        // Update existing card using store
        await updateCard(currentCardId, {
          cardName: cardName.trim(),
          cardData: cleanedFormData,
        }, token);
        cardIdToUse = currentCardId;
      }

      // Step 2: Generate QR codes using store
      const qrData = await generateQR(
        cardIdToUse,
        token,
        {
          primary: primaryColor,
          secondary: secondaryColor
        }
      );
      
      // Update QR code state
      setQrCode(qrData.vCardQR);

      setSuccess(true);
      setIsDirty(false);
      
      // Navigate to dashboard after creating a new card
      if (isNewCard || !currentCardId) {
        // Wait briefly to show success message, then navigate
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        // Switch back to view mode after successful update
        cleanupFormData();
        setIsEditMode(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save card and generate QR code');
    } finally {
      setLoading(false);
    }
  };

  // Download QR as PNG
  const handleDownloadPNG = async () => {
    if (!qrCode) return;
    setDownloadLoading(true);

    try {
      const brandedImage = await createBrandedCardImage({
        qrCodeUrl: qrCode,
        formData,
        primaryColor,
        secondaryColor,
        azureProfileId: azureProfile?.id,
        getAccessToken: () => getAccessToken(false)
      }, 'png');
      
      const link = document.createElement('a');
      link.href = brandedImage;
      link.download = generateCardFileName(formData.displayName, 'png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setError('Failed to download as PNG');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Download QR as JPEG
  const handleDownloadJPEG = async () => {
    if (!qrCode) return;
    setDownloadLoading(true);

    try {
      const brandedImage = await createBrandedCardImage({
        qrCodeUrl: qrCode,
        formData,
        primaryColor,
        secondaryColor,
        azureProfileId: azureProfile?.id,
        getAccessToken: () => getAccessToken(false)
      }, 'jpeg');
      
      const link = document.createElement('a');
      link.href = brandedImage;
      link.download = generateCardFileName(formData.displayName, 'jpg');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setError('Failed to download as JPEG');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Download QR as SVG
  const handleDownloadSVG = async () => {
    if (!qrCode) return;
    setDownloadLoading(true);

    try {
      // Create branded card and convert to SVG
      const brandedImage = await createBrandedCardImage({
        qrCodeUrl: qrCode,
        formData,
        primaryColor,
        secondaryColor,
        azureProfileId: azureProfile?.id,
        getAccessToken: () => getAccessToken(false)
      }, 'png');
      
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="700" viewBox="0 0 1200 700">
          <image href="${brandedImage}" width="1200" height="700"/>
        </svg>
      `;
      
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = generateCardFileName(formData.displayName, 'svg');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      setError('Failed to download as SVG');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Download as vCard (.vcf)
  const handleDownloadVCard = () => {
    try {
      downloadVCard(formData);
    } catch (error) {
      setError('Failed to download vCard');
    }
  };

  // Download as PDF with QR and contact details
  const handleDownloadPDF = async () => {
    if (!qrCode) return;
    setDownloadLoading(true);

    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Add title
      pdf.setFontSize(20);
      pdf.setTextColor(30, 58, 138); // Primary color
      pdf.text('Digital Business Card', pageWidth / 2, 20, { align: 'center' });

      // Add QR Code
      const img = document.createElement('img');
      img.src = qrCode;
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      const qrSize = 80;
      const qrX = (pageWidth - qrSize) / 2;
      pdf.addImage(qrCode, 'PNG', qrX, 30, qrSize, qrSize);

      // Add contact information
      let yPos = 120;
      const leftMargin = 20;
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);

      // Name
      pdf.setFont('helvetica', 'bold');
      pdf.text(formData.displayName, leftMargin, yPos);
      yPos += 8;

      // Job title and company
      pdf.setFont('helvetica', 'normal');
      if (formData.jobTitle) {
        pdf.text(`${formData.jobTitle}${formData.companyName ? ' at ' + formData.companyName : ''}`, leftMargin, yPos);
        yPos += 6;
      }

      yPos += 4;

      // Contact details
      if (formData.mail) {
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Email: ${formData.mail.trim()}`, leftMargin, yPos);
        yPos += 6;
      }

      if (formData.mobilePhone) {
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Phone: ${formData.mobilePhone.trim()}`, leftMargin, yPos);
        yPos += 6;
      }

      if (formData.website) {
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Website: ${formData.website.trim()}`, leftMargin, yPos);
        yPos += 6;
      }

      // Address
      if (formData.address) {
        yPos += 4;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Address:', leftMargin, yPos);
        yPos += 6;
        pdf.setFont('helvetica', 'normal');
        const addressLines = [
          formData.address.street,
          `${formData.address.city}, ${formData.address.state} ${formData.address.postalCode}`,
          formData.address.country
        ].filter(Boolean);
        
        addressLines.forEach(line => {
          if (line) {
            pdf.text(line, leftMargin, yPos);
            yPos += 6;
          }
        });
      }

      // Footer
      pdf.setFontSize(10);
      pdf.setTextColor(128, 128, 128);
      pdf.text('Scan the QR code to save contact information', pageWidth / 2, pageHeight - 20, { align: 'center' });

      pdf.save(generateCardFileName(formData.displayName, 'pdf'));
    } catch (error) {
      setError('Failed to generate PDF');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Copy QR to clipboard
  const handleCopyToClipboard = async () => {
    if (!qrCode) return;
    setDownloadLoading(true);

    try {
      const brandedImage = await createBrandedCardImage({
        qrCodeUrl: qrCode,
        formData,
        primaryColor,
        secondaryColor,
        azureProfileId: azureProfile?.id,
        getAccessToken: () => getAccessToken(false)
      }, 'png');
      
      const response = await fetch(brandedImage);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob
        })
      ]);
      setSuccess(true);
      setError(null);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setError('Failed to copy to clipboard. Your browser may not support this feature.');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Share via Web Share API
  const handleShare = async () => {
    if (!qrCode) return;
    setDownloadLoading(true);

    try {
      if (!navigator.share) {
        throw new Error('Web Share API is not supported in your browser');
      }

      const brandedImage = await createBrandedCardImage({
        qrCodeUrl: qrCode,
        formData,
        primaryColor,
        secondaryColor,
        azureProfileId: azureProfile?.id,
        getAccessToken: () => getAccessToken(false)
      }, 'png');
      
      const response = await fetch(brandedImage);
      const blob = await response.blob();
      const file = new File([blob], generateCardFileName(formData.displayName, 'png'), { type: 'image/png' });

      await navigator.share({
        title: `${formData.displayName} - Business Card`,
        text: `Digital business card for ${formData.displayName}`,
        files: [file]
      });
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        setError(error.message || 'Failed to share. Your browser may not support this feature.');
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  // Email QR code
  const handleEmailQR = async () => {
    if (!qrCode) return;
    setDownloadLoading(true);

    try {
      const brandedImage = await createBrandedCardImage({
        qrCodeUrl: qrCode,
        formData,
        primaryColor,
        secondaryColor,
        azureProfileId: azureProfile?.id,
        getAccessToken: () => getAccessToken(false)
      }, 'png');

      const fileName = generateCardFileName(formData.displayName, 'png');

      // Download the card image
      const link = document.createElement('a');
      link.href = brandedImage;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Open email client after download starts
      setTimeout(() => {
        const subject = encodeURIComponent(`Business Card - ${formData.displayName}`);
        const body = encodeURIComponent(
          `Hi,\n\nPlease find my digital business card attached (just downloaded to your device).\n\n` +
          `Name: ${formData.displayName}\n` +
          `${formData.jobTitle ? `Title: ${formData.jobTitle}\n` : ''}` +
          `${formData.companyName ? `Company: ${formData.companyName}\n` : ''}` +
          `${formData.mail ? `Email: ${formData.mail}\n` : ''}` +
          `${formData.mobilePhone ? `Phone: ${formData.mobilePhone}\n` : ''}` +
          `\nBest regards,\n${formData.displayName}`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      }, 1000);

    } catch (error: any) {
      if (error.name !== 'AbortError') {
        setError(error.message || 'Failed to prepare email.');
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="card-manager-container">
      <div className="card-manager-header">
        <button onClick={() => navigate(fromRoute)} className="btn-back">
          <ArrowLeft size={20} />
          <span>Back to {fromRoute === '/cards' ? 'My Cards' : 'Dashboard'}</span>
        </button>
        <div className="header-content-main">
          <div className="header-title-row">
            <h1>
              <QrCode size={32} />
              {!isEditMode ? 'View Business Card' : (isEdit ? 'Edit Business Card' : isNewCard ? 'Create New Card' : 'Card Manager')}
            </h1>
            <div className="header-actions">
              {!isEditMode && !isNewCard && (
                <div className="mode-indicator-badge view-mode">
                  <span className="mode-dot"></span>
                  <span>View Mode</span>
                </div>
              )}
              {isEditMode && !isNewCard && isEdit && (
                <div className="mode-indicator-badge edit-mode">
                  <span className="mode-dot"></span>
                  <span>Edit Mode</span>
                </div>
              )}
              {!isEditMode && !isNewCard && (
                <button onClick={handleToggleEditMode} className="btn-edit-mode">
                  <Edit size={16} />
                  <span>Edit Card</span>
                </button>
              )}
              {isEditMode && !isNewCard && isEdit && (
                <button onClick={handleToggleEditMode} className="btn-cancel-edit">
                  <X size={16} />
                  <span>Cancel Edit</span>
                </button>
              )}
            </div>
          </div>
          <div className="header-description-row">
            <p className="header-description">
              {!isEditMode
                ? 'View your business card details and QR code'
                : isEdit 
                ? 'Update your business card information and generate a new QR code' 
                : isNewCard 
                ? 'Create a new digital business card with QR code' 
                : 'Manage your digital business cards and QR codes'}
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <Loading message="Processing..." />
      ) : (
        <div className="card-manager-content">
          <div className={`form-section card ${!isEditMode ? 'view-mode-form' : 'edit-mode-form'}`}>
            {/* Validation Summary Banner */}
            {Object.keys(validationErrors).length > 0 && (
              <div className="validation-summary-banner">
                <div className="validation-header">
                  <AlertCircle size={20} />
                  <strong>Please fix the following errors:</strong>
                </div>
                <ul className="validation-list">
                  {Object.entries(validationErrors).map(([key, message]) => (
                    <li key={key}>{message}</li>
                  ))}
                </ul>
                <p className="validation-footer">Scroll down to see highlighted fields and correct the errors.</p>
              </div>
            )}

            <FormSectionHeader icon={User} title="Card Information" />
            
            {/* Card Name Field */}
            <ValidatedInput
                label="Card Name"
                type="text"
                value={cardName}
                onChange={(value) => {
                  if (!isEditMode) return;
                  if (value.length <= 50) {
                    setCardName(value);
                    setIsDirty(true);
                  }
                  if (validationErrors.cardName) {
                    clearValidationError('cardName');
                  }
                }}
                placeholder="e.g., Work Card, Personal Card, Conference Card"
                error={validationErrors.cardName}
                helperText={isEditMode ? "Give this card a memorable name (max 50 characters)" : undefined}
                maxLength={50}
                readOnly={!isEditMode}
                required
                className="no-label"
              />
            
            <FormSectionHeader icon={User} title="Basic Information" />
          <div className="form-grid">
            <div className="form-group">
              <label className="label">
                Display Name <span className="required-asterisk">*</span>
                {formData.displayName 
                  ? <small className="readonly-note">{isFromScan ? '🔒 From Azure AD' : 'From Azure AD'}</small>
                  : <small className="not-available-note">Not present in Azure AD</small>
                }
              </label>
              <input
                type="text"
                className={`input ${formData.displayName ? 'readonly-field' : (!isEditMode ? 'readonly-field empty-field' : 'editable-empty')}`}
                value={formData.displayName}
                onChange={(e) => { if (isEditMode && !formData.displayName) handleInputChange('displayName', e.target.value); }}
                placeholder={formData.displayName ? '' : 'Enter your name'}
                readOnly={!!formData.displayName || !isEditMode}
                maxLength={100}
                title={formData.displayName ? 'Synced from Azure AD' : 'Add your display name'}
              />
            </div>

            <div className="form-group">
              <label className="label">
                First Name <span className="required-asterisk">*</span>
                {formData.givenName 
                  ? <small className="readonly-note">{isFromScan ? '🔒 From Azure AD' : 'From Azure AD'}</small>
                  : <small className="not-available-note">Not present in Azure AD</small>
                }
              </label>
              <input
                type="text"
                className={`input ${formData.givenName ? 'readonly-field' : (!isEditMode ? 'readonly-field empty-field' : 'editable-empty')}`}
                value={formData.givenName || ''}
                onChange={(e) => { if (isEditMode && !formData.givenName) handleInputChange('givenName', e.target.value); }}
                placeholder={formData.givenName ? '' : 'Enter first name'}
                readOnly={!!formData.givenName || !isEditMode}
                maxLength={50}
                title={formData.givenName ? 'Synced from Azure AD' : 'Add your first name'}
              />
            </div>

            <div className="form-group">
              <label className="label">
                Last Name <span className="required-asterisk">*</span>
                {formData.surname 
                  ? <small className="readonly-note">{isFromScan ? '🔒 From Azure AD' : 'From Azure AD'}</small>
                  : <small className="not-available-note">Not present in Azure AD</small>
                }
              </label>
              <input
                type="text"
                className={`input ${formData.surname ? 'readonly-field' : (!isEditMode ? 'readonly-field empty-field' : 'editable-empty')}`}
                value={formData.surname || ''}
                onChange={(e) => { if (isEditMode && !formData.surname) handleInputChange('surname', e.target.value); }}
                placeholder={formData.surname ? '' : 'Enter last name'}
                readOnly={!!formData.surname || !isEditMode}
                maxLength={50}
                title={formData.surname ? 'Synced from Azure AD' : 'Add your last name'}
              />
            </div>

            <div className="form-group">
              <ValidatedInput
                label="Job Title"
                type="text"
                value={formData.jobTitle || ''}
                onChange={(value) => isEditMode && handleInputChange('jobTitle', value)}
                placeholder="Software Engineer"
                readOnly={!isEditMode}
                maxLength={100}
              />
              {isFromScan && !formData.jobTitle && azureProfile?.jobTitle && (
                <small className="azure-ad-hint">
                  💡 Job Title from your profile: "{azureProfile.jobTitle}"
                  <button type="button" className="btn-use-azure" onClick={() => handleInputChange('jobTitle', azureProfile.jobTitle!)}>Use</button>
                </small>
              )}
            </div>

            <div className="form-group">
              <ValidatedInput
                label="Department"
                type="text"
                value={formData.department || ''}
                onChange={(value) => isEditMode && handleInputChange('department', value)}
                placeholder="Engineering"
                readOnly={!isEditMode}
                required
              />
              {isFromScan && !formData.department && azureProfile?.department && (
                <small className="azure-ad-hint">
                  💡 Department from your profile: "{azureProfile.department}"
                  <button type="button" className="btn-use-azure" onClick={() => handleInputChange('department', azureProfile.department!)}>Use</button>
                </small>
              )}
            </div>

            {isFromScan && (
              <div className="form-group">
                <ValidatedInput
                  label="Company Name"
                  type="text"
                  value={formData.companyName || ''}
                  onChange={(value) => isEditMode && handleInputChange('companyName', value)}
                  placeholder="Company name"
                  readOnly={!isEditMode}
                  maxLength={100}
                />
                {!formData.companyName && azureProfile?.companyName && (
                  <small className="azure-ad-hint">
                    💡 Company from your profile: "{azureProfile.companyName}"
                    <button type="button" className="btn-use-azure" onClick={() => handleInputChange('companyName', azureProfile.companyName!)}>Use</button>
                  </small>
                )}
              </div>
            )}
          </div>

          {/* Company Information Section */}
          {companyInfo && (
            <>
              <FormSectionHeader icon={User} title="Company Information" subtitle="(From Organization)" />
              <div className="form-grid">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="label">Company Name <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    className="input readonly-field"
                    value={companyInfo.companyName}
                    readOnly
                    title={companyInfo.companyName}
                  />
                </div>

                <div className="form-group">
                  <label className="label">Business Sector <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    className="input readonly-field"
                    value={companyInfo.businessSector}
                    readOnly
                    title="This field is from your organization profile"
                  />
                </div>

                <div className="form-group">
                  <label className="label">Location <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    className="input readonly-field"
                    value={`${companyInfo.city}, ${companyInfo.country}`}
                    readOnly
                    title="This field is from your organization profile"
                  />
                </div>

                <div className="form-group">
                  <label className="label">Company Code <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    className="input readonly-field"
                    value={companyInfo.shortCode}
                    readOnly
                    title="This field is from your organization profile"
                  />
                </div>
              </div>
            </>
          )}

          <FormSectionHeader icon={Mail} title="Contact Information" />
          <div className="form-grid">
            <div className="form-group">
              <label className="label">
                Primary Email <span className="required-asterisk">*</span>
                {formData.mail 
                  ? <small className="readonly-note">{isFromScan ? '🔒 From Azure AD' : 'From Azure AD'}</small>
                  : <small className="not-available-note">Not present in Azure AD</small>
                }
              </label>
              <input
                type="email"
                className={`input ${formData.mail ? 'readonly-field' : (!isEditMode ? 'readonly-field empty-field' : 'editable-empty')}`}
                value={formData.mail || ''}
                onChange={(e) => { if (isEditMode && !formData.mail) handleInputChange('mail', e.target.value); }}
                placeholder={formData.mail ? '' : 'Enter your email address'}
                readOnly={!!formData.mail || !isEditMode}
                title={formData.mail ? 'Synced from Azure AD' : 'Add your email address'}
              />
            </div>

            <div className="form-group">
              <label className="label">
                Mobile Phone <span className="required-asterisk">*</span>
                {formData.mobilePhone 
                  ? <small className="readonly-note">{isFromScan ? 'From scanned card' : 'From Azure AD'}</small>
                  : <small className="not-available-note">Not present in Azure AD</small>
                }
              </label>
              <input
                type="tel"
                className={`input ${formData.mobilePhone && !isFromScan ? 'readonly-field' : (!isEditMode ? 'readonly-field empty-field' : (formData.mobilePhone ? '' : 'editable-empty'))} ${validationErrors.mobilePhone ? 'input-error' : ''}`}
                value={formData.mobilePhone || ''}
                onChange={(e) => {
                  if (!isEditMode) return;
                  handleInputChange('mobilePhone', e.target.value);
                  if (validationErrors.mobilePhone) clearValidationError('mobilePhone');
                }}
                onBlur={() => {
                  if (!isEditMode) return;
                  const val = formData.mobilePhone || '';
                  if (val.trim() && !validatePhone(val)) {
                    setValidationErrors(prev => ({ ...prev, mobilePhone: 'Invalid phone format (7-15 digits)' }));
                  }
                }}
                placeholder={formData.mobilePhone ? '' : 'Enter your phone number'}
                maxLength={20}
                readOnly={(!!formData.mobilePhone && !isFromScan) || !isEditMode}
                title={formData.mobilePhone ? (isFromScan ? 'From scanned card' : 'Synced from Azure AD') : 'Add your phone number'}
              />
              {validationErrors.mobilePhone && (
                <small className="error-text">{validationErrors.mobilePhone}</small>
              )}
              {isFromScan && !formData.mobilePhone && azureProfile?.mobilePhone && (
                <small className="azure-ad-hint">
                  💡 Phone from your profile: "{azureProfile.mobilePhone}"
                  <button type="button" className="btn-use-azure" onClick={() => handleInputChange('mobilePhone', azureProfile.mobilePhone!)}>Use</button>
                </small>
              )}
            </div>
          </div>

          {/* Additional Emails */}
          <DynamicFieldList
            title="Additional Emails"
            items={formData.additionalEmails || []}
            readOnly={!isEditMode}
            onAdd={addEmail}
            onRemove={removeEmail}
            addButtonText="Add Email"
            emptyMessage="No additional emails added yet"
            disableAdd={(formData.additionalEmails?.length ?? 0) >= 5}
            renderItem={(email, index) => (
              <>
                <div className="dynamic-item">
                  <input
                    type="email"
                    className={`input ${!isEditMode ? 'readonly-field' : ''} ${validationErrors[`additionalEmail_${index}`] ? 'input-error' : ''}`}
                    value={email}
                    name={`additional-email-${index}`}
                    autoComplete="off"
                    onChange={(e) => {
                      if (!isEditMode) return;
                      updateEmail(index, e.target.value);
                      if (validationErrors[`additionalEmail_${index}`]) {
                        clearValidationError(`additionalEmail_${index}`);
                      }
                    }}
                    onBlur={(e) => {
                      if (!isEditMode) return;
                      const value = e.target.value;
                      if (value.trim() && !validateEmail(value)) {
                        setValidationErrors(prev => ({
                          ...prev,
                          [`additionalEmail_${index}`]: 'Invalid email format'
                        }));
                      }
                    }}
                    placeholder="additional@email.com"
                    maxLength={254}
                    readOnly={!isEditMode}
                  />
                  {isEditMode && (
                    <button onClick={() => removeEmail(index)} className="btn-remove">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                {validationErrors[`additionalEmail_${index}`] && (
                  <small className="error-text">{validationErrors[`additionalEmail_${index}`]}</small>
                )}
                {isEditMode && !validationErrors[`additionalEmail_${index}`] && email && email.trim() && email.includes('@') && (
                  <small className="success-text">✓ Valid email format</small>
                )}
              </>
            )}
          />

          {/* Additional Phones */}
          <DynamicFieldList
            title="Additional Phone Numbers"
            items={formData.additionalPhones || []}
            readOnly={!isEditMode}
            onAdd={addPhone}
            onRemove={removePhone}
            addButtonText="Add Phone"
            emptyMessage="No additional phone numbers added yet"
            disableAdd={(formData.additionalPhones?.length ?? 0) >= 5}
            renderItem={(phone, index) => (
              <>
                <div className="dynamic-item phone-item">
                  <select
                    className={`input ${!isEditMode ? 'readonly-field' : ''}`}
                    value={phone.type}
                    onChange={(e) => isEditMode && updatePhone(index, 'type', e.target.value)}
                    disabled={!isEditMode}
                  >
                    <option value="HOME">Home</option>
                    <option value="WORK">Work</option>
                    <option value="CELL">Cell</option>
                    <option value="FAX">Fax</option>
                  </select>
                  <input
                    type="tel"
                    className={`input ${!isEditMode ? 'readonly-field' : ''} ${validationErrors[`additionalPhone_${index}`] ? 'input-error' : ''}`}
                    value={phone.number}
                    onChange={(e) => {
                      if (!isEditMode) return;
                      const value = e.target.value;
                      updatePhone(index, 'number', value);
                      if (validationErrors[`additionalPhone_${index}`]) {
                        clearValidationError(`additionalPhone_${index}`);
                      }
                    }}
                    onBlur={(e) => {
                      if (!isEditMode) return;
                      const value = e.target.value;
                      if (value.trim() && !validatePhone(value)) {
                        setValidationErrors(prev => ({
                          ...prev,
                          [`additionalPhone_${index}`]: 'Invalid phone format. Must be 7-15 digits.'
                        }));
                      }
                    }}
                    placeholder="+1234567890"
                    maxLength={20}
                    readOnly={!isEditMode}
                  />
                  {isEditMode && (
                    <button onClick={() => removePhone(index)} className="btn-remove">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                {validationErrors[`additionalPhone_${index}`] && (
                  <small className="error-text">{validationErrors[`additionalPhone_${index}`]}</small>
                )}
                {isEditMode && !validationErrors[`additionalPhone_${index}`] && phone.number && phone.number.trim() && (
                  <small className="success-text">✓ Valid phone format</small>
                )}
              </>
            )}
          />

          {/* Primary Address */}
          <h2><MapPin size={24} /> Address</h2>
          {!formData.address ? (
            <>
              {isEditMode && (
                <button onClick={() => addAddress(true)} className="btn btn-outline">
                  <Plus size={16} /> Add Primary Address
                </button>
              )}
            </>
          ) : (
            <AddressForm
              address={formData.address}
              index="primary"
              readOnly={!isEditMode}
              validationErrors={validationErrors}
              onUpdate={updateAddress}
              onRemove={removeAddress}
              onClearError={clearValidationError}
            />
          )}

          {/* Additional Addresses */}
          <DynamicFieldList
            title="Additional Addresses"
            items={formData.additionalAddresses || []}
            readOnly={!isEditMode}
            onAdd={() => addAddress(false)}
            onRemove={removeAddress}
            addButtonText="Add Address"
            emptyMessage="No additional addresses added yet"
            disableAdd={(formData.additionalAddresses?.length ?? 0) >= 4}
            renderItem={(address, index) => (
              <AddressForm
                key={index}
                address={address}
                index={index}
                readOnly={!isEditMode}
                validationErrors={validationErrors}
                onUpdate={updateAddress}
                onRemove={removeAddress}
                onClearError={clearValidationError}
              />
            )}
          />

          {/* Website and Social */}
          <h2><Globe size={24} /> Online Presence</h2>
          
          <div className="full-width-group">
            <ValidatedInput
              label="Website"
              type="url"
              value={formData.website || ''}
              onChange={(value) => {
                if (!isEditMode) return;
                handleInputChange('website', value);
                if (validationErrors.website) {
                  clearValidationError('website');
                }
              }}
              placeholder="https://example.com"
              error={validationErrors.website}
              helperText={isEditMode ? "Must include http:// or https:// (e.g., https://example.com)" : undefined}
              readOnly={!isEditMode}
              maxLength={500}
            />
          </div>

          <DynamicFieldList
            title="Social Profiles"
            items={formData.socialProfiles || []}
            readOnly={!isEditMode}
            onAdd={addSocialProfile}
            onRemove={removeSocialProfile}
            addButtonText="Add Profile"
            emptyMessage="No social profiles added yet"
            disableAdd={(formData.socialProfiles?.length ?? 0) >= 4}
            renderItem={(profile, index) => (
              <>
                <div className="dynamic-item phone-item">
                  <select
                    className={`input ${!isEditMode ? 'readonly-field' : ''}`}
                    value={profile.platform}
                    onChange={(e) => isEditMode && updateSocialProfile(index, 'platform', e.target.value)}
                    disabled={!isEditMode}
                  >
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Twitter">Twitter</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Instagram">Instagram</option>
                    <option value="GitHub">GitHub</option>
                    <option value="Other">Other</option>
                  </select>
                  <input
                    type="url"
                    className={`input ${!isEditMode ? 'readonly-field' : ''} ${validationErrors[`socialProfile_${index}`] ? 'input-error' : ''}`}
                    value={profile.url}
                    onChange={(e) => {
                      if (!isEditMode) return;
                      updateSocialProfile(index, 'url', e.target.value);
                      if (validationErrors[`socialProfile_${index}`]) {
                        clearValidationError(`socialProfile_${index}`);
                      }
                    }}
                    placeholder="https://..."
                    maxLength={500}
                    readOnly={!isEditMode}
                  />
                  {isEditMode && (
                    <button onClick={() => removeSocialProfile(index)} className="btn-remove">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                {validationErrors[`socialProfile_${index}`] && (
                  <small className="error-text">{validationErrors[`socialProfile_${index}`]}</small>
                )}
                {isEditMode && !validationErrors[`socialProfile_${index}`] && profile.url && profile.url.trim() && (profile.url.startsWith('http://') || profile.url.startsWith('https://')) && (
                  <small className="success-text">✓ Valid URL format</small>
                )}
              </>
            )}
          />

          {/* Notes */}
          <div className="full-width-group">
            <div className="form-group">
              <label className="label">Notes</label>
              <textarea
                className={`input ${!isEditMode ? 'readonly-field' : ''}`}
                value={formData.notes || ''}
                onChange={(e) => {
                  if (!isEditMode) return;
                  if (e.target.value.length <= 500) {
                    handleInputChange('notes', e.target.value);
                  }
                }}
                placeholder="Additional information... (max 500 characters)"
                rows={3}
                maxLength={500}
                readOnly={!isEditMode}
              />
            </div>
          </div>

          {/* Save Button - Desktop (After Form) */}
          {isEditMode && (
            <div className="desktop-save-button">
              <button 
                onClick={handleGenerateQR} 
                className="btn-save-desktop"
                disabled={loading || !formData.displayName || !cardName.trim()}
              >
                <Save size={20} />
                <span>
                  {loading ? 'Saving...' : (isEdit ? 'Update Card' : isNewCard ? 'Create Card' : 'Save Card')}
                </span>
              </button>
            </div>
          )}

          {/* Save Button - Mobile Only (Inside form) */}
          {isEditMode && (
            <div className="mobile-save-button">
              <button 
                onClick={handleGenerateQR} 
                className="btn-save-mobile"
                disabled={loading || !formData.displayName || !cardName.trim()}
              >
                <Save size={20} />
                <span>
                  {loading ? 'Saving...' : (isEdit ? 'Update Card' : isNewCard ? 'Create Card' : 'Save Card')}
                </span>
              </button>
            </div>
          )}

          {/* Colors - Hidden for current release */}
          {/* <h2>QR Code Customization</h2>
          <div className="color-picker-section">
            <div className="color-input-group">
              <label className="label">Primary Color</label>
              <div className="color-input">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                />
                <input
                  type="text"
                  className="input"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#1E3A8A"
                />
              </div>
            </div>
            <div className="color-input-group">
              <label className="label">Secondary Color</label>
              <div className="color-input">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                />
                <input
                  type="text"
                  className="input"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  placeholder="#FFFFFF"
                />
              </div>
            </div>
          </div> */}
        </div>

        {/* Preview Section */}
        <div className="preview-section card">
          <div className="preview-header">
            <h2>QR Code Preview</h2>
            {isEditMode && (
              <button 
                onClick={handleGenerateQR} 
                className="btn-regenerate-header"
                disabled={loading || !formData.displayName || !cardName.trim()}
              >
                <Save size={16} />
                <span>
                  {loading ? 'Saving...' : (isEdit ? 'Update Card' : isNewCard ? 'Create Card' : 'Save Card')}
                </span>
              </button>
            )}
          </div>
          
          {loading && <Loading message="Generating QR code..." />}
          
          {error && (
            <div className="alert alert-error">
              <AlertCircle size={20} />
              <p>{error}</p>
            </div>
          )}
          
          {success && (
            <div className="alert alert-success">
              <CheckCircle2 size={20} />
              <p>QR codes generated successfully!</p>
            </div>
          )}

          {qrCode ? (
            <div className="qr-preview">

              <img 
                src={qrCode} 
                alt="Business Card QR Code" 
                className="qr-preview-image clickable"
                onClick={() => setShowQrModal(true)}
                title="Click to view larger"
                style={{ cursor: 'pointer' }}
              />
              
              <div className="download-heading">
                <h3>Download QR Code</h3>
                <p>Includes your details with QR code</p>
              </div>
              
              <div className="download-options">
                <div className="download-section">
                  <h4>Image Formats</h4>
                  <div className="download-icons">
                    <button onClick={handleDownloadPNG} className="icon-btn" title="Download branded card as PNG with contact details" disabled={downloadLoading}>
                      <Image size={20} />
                      <span>PNG</span>
                    </button>
                    <button onClick={handleDownloadJPEG} className="icon-btn" title="Download branded card as JPEG with contact details" disabled={downloadLoading}>
                      <Image size={20} />
                      <span>JPEG</span>
                    </button>
                    <button onClick={handleDownloadSVG} className="icon-btn" title="Download branded card as SVG with contact details" disabled={downloadLoading}>
                      <Image size={20} />
                      <span>SVG</span>
                    </button>
                  </div>
                </div>

                <div className="download-section">
                  <h4>Contact Formats</h4>
                  <div className="download-icons">
                    <button onClick={handleDownloadVCard} className="icon-btn" title="Download vCard" disabled={downloadLoading}>
                      <FileText size={20} />
                      <span>vCard</span>
                    </button>
                    <button onClick={handleDownloadPDF} className="icon-btn" title="Download PDF" disabled={downloadLoading}>
                      <FileText size={20} />
                      <span>PDF</span>
                    </button>
                  </div>
                </div>

                <div className="download-section">
                  <h4>Share Options</h4>
                  <div className="download-icons">
                    <button onClick={handleCopyToClipboard} className="icon-btn" title="Copy to Clipboard" disabled={downloadLoading}>
                      <Copy size={20} />
                      <span>Copy</span>
                    </button>
                    <button onClick={handleShare} className="icon-btn" title="Share" disabled={downloadLoading}>
                      <Share2 size={20} />
                      <span>Share</span>
                    </button>
                    <button onClick={handleEmailQR} className="icon-btn" title="Email" disabled={downloadLoading}>
                      <Mail size={20} />
                      <span>Email</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="qr-placeholder">
              <p>Your QR code will appear here</p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* QR Code Modal */}
      <QRModal
        isOpen={showQrModal && !!qrCode}
        qrCodeUrl={qrCode || ''}
        title="Scan vCard QR Code"
        description="Scan to save contact directly to your device"
        onClose={() => setShowQrModal(false)}
        onDownload={handleDownloadPNG}
        onDownloadVCard={handleDownloadVCard}
        onShare={handleShare}
        onEmail={handleEmailQR}
      />

      {/* Empty fields confirmation dialog */}
      {showEmptyFieldsConfirm && (
        <div className="modal-overlay" onClick={() => setShowEmptyFieldsConfirm(false)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="confirm-header">
              <AlertCircle size={24} />
              <h3>Empty Fields Detected</h3>
            </div>
            <p>Some added fields are empty and will be removed on save. Would you like to continue?</p>
            <div className="confirm-actions">
              <button className="btn-cancel-edit" onClick={() => setShowEmptyFieldsConfirm(false)}>
                Cancel
              </button>
              <button className="btn-edit-mode" onClick={handleGenerateQR}>
                Continue & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardEditor;
